import { MetricPillInput } from "../../../../components/metrics/MetricPillInput";
import { RatingRow } from "../../../../components/metrics/RatingRow";
import {
  COMMON_VALUES,
  METRIC_RANGES,
  METRIC_UNITS,
} from "../../../../constants/metrics";
import type {
  ProspectPositionPlayerMetricsResponse,
  MetricEntryResponse,
} from "../../../../types/api";
import "./PositionPlayerMetricsSection.css";

interface PositionPlayerMetricsSectionProps {
  metrics: Partial<ProspectPositionPlayerMetricsResponse>;
  onMetricChange: (field: string, value: number | null) => void;
  /** Optional: prospect ID for metric history tracking */
  prospectId?: string;
  /** Optional: all metric entries for the prospect (from prospect response) */
  metricEntries?: MetricEntryResponse[];
  /** Optional: Current user ID to show "You" for entries recorded by the logged-in user */
  currentUserId?: string;
  /** Optional: organization sport to determine dash distance label */
  sport?: string | null;
}

export function PositionPlayerMetricsSection({
  metrics,
  onMetricChange,
  prospectId,
  metricEntries = [],
  currentUserId,
  sport,
}: PositionPlayerMetricsSectionProps) {
  return (
    <div className="position-player-metrics">
      {/* Settings Section */}
      <div className="position-player-metrics__section">
        <h4 className="position-player-metrics__section-title">Settings</h4>

        <MetricPillInput
          label="Home-to-First"
          value={metrics.homeToFirst ?? null}
          commonValues={COMMON_VALUES.homeToFirst}
          unit={METRIC_UNITS.time}
          onChange={(value) => onMetricChange("homeToFirst", value)}
          min={METRIC_RANGES.time.min}
          max={METRIC_RANGES.time.max}
          decimalPlaces={2}
          prospectId={prospectId}
          metricName="homeToFirst"
          metricCategory="position_player"
          metricEntries={metricEntries}
          currentUserId={currentUserId}
        />

        <MetricPillInput
          label="Pop Time"
          value={metrics.popTime ?? null}
          commonValues={COMMON_VALUES.popTime}
          unit={METRIC_UNITS.time}
          onChange={(value) => onMetricChange("popTime", value)}
          min={METRIC_RANGES.time.min}
          max={METRIC_RANGES.time.max}
          decimalPlaces={2}
          prospectId={prospectId}
          metricName="popTime"
          metricCategory="position_player"
          metricEntries={metricEntries}
          currentUserId={currentUserId}
        />
      </div>

      {/* 20/80 Scale Ratings */}
      <div className="position-player-metrics__section">
        <h4 className="position-player-metrics__section-title">
          20/80 Scale Grades
        </h4>

        <div className="position-player-metrics__rating-header">
          <span className="position-player-metrics__rating-header-label"></span>
          <div className="position-player-metrics__rating-header-columns">
            <span className="position-player-metrics__rating-header-column">
              Present
            </span>
            <span className="position-player-metrics__rating-header-column">
              Future
            </span>
          </div>
        </div>

        <RatingRow
          label="Hit:"
          presentValue={metrics.hitPresent ?? null}
          futureValue={metrics.hitFuture ?? null}
          onPresentChange={(value) => onMetricChange("hitPresent", value)}
          onFutureChange={(value) => onMetricChange("hitFuture", value)}
        />

        <RatingRow
          label="Power:"
          presentValue={metrics.powerPresent ?? null}
          futureValue={metrics.powerFuture ?? null}
          onPresentChange={(value) => onMetricChange("powerPresent", value)}
          onFutureChange={(value) => onMetricChange("powerFuture", value)}
        />

        <RatingRow
          label="Field:"
          presentValue={metrics.fieldPresent ?? null}
          futureValue={metrics.fieldFuture ?? null}
          onPresentChange={(value) => onMetricChange("fieldPresent", value)}
          onFutureChange={(value) => onMetricChange("fieldFuture", value)}
        />

        <RatingRow
          label="Throw:"
          presentValue={metrics.throwPresent ?? null}
          futureValue={metrics.throwFuture ?? null}
          onPresentChange={(value) => onMetricChange("throwPresent", value)}
          onFutureChange={(value) => onMetricChange("throwFuture", value)}
        />

        <RatingRow
          label="Run:"
          presentValue={metrics.runPresent ?? null}
          futureValue={metrics.runFuture ?? null}
          onPresentChange={(value) => onMetricChange("runPresent", value)}
          onFutureChange={(value) => onMetricChange("runFuture", value)}
        />
      </div>

      {/* Velocities and Times */}
      <div className="position-player-metrics__section">
        <h4 className="position-player-metrics__section-title">
          Velocity & Time Metrics
        </h4>

        <MetricPillInput
          label="Exit Velocity"
          value={metrics.exitVelocity ?? null}
          commonValues={COMMON_VALUES.exitVelocity}
          unit={METRIC_UNITS.velocity}
          onChange={(value) => onMetricChange("exitVelocity", value)}
          min={METRIC_RANGES.velocity.min}
          max={METRIC_RANGES.velocity.max}
          decimalPlaces={1}
          prospectId={prospectId}
          metricName="exitVelocity"
          metricCategory="position_player"
          metricEntries={metricEntries}
          currentUserId={currentUserId}
        />

        <MetricPillInput
          label="IF Velocity"
          value={metrics.infieldVelocity ?? null}
          commonValues={COMMON_VALUES.infieldVelocity}
          unit={METRIC_UNITS.velocity}
          onChange={(value) => onMetricChange("infieldVelocity", value)}
          min={METRIC_RANGES.velocity.min}
          max={METRIC_RANGES.velocity.max}
          decimalPlaces={1}
          prospectId={prospectId}
          metricName="infieldVelocity"
          metricCategory="position_player"
          metricEntries={metricEntries}
          currentUserId={currentUserId}
        />

        <MetricPillInput
          label="OF Velocity"
          value={metrics.outfieldVelocity ?? null}
          commonValues={COMMON_VALUES.outfieldVelocity}
          unit={METRIC_UNITS.velocity}
          onChange={(value) => onMetricChange("outfieldVelocity", value)}
          min={METRIC_RANGES.velocity.min}
          max={METRIC_RANGES.velocity.max}
          decimalPlaces={1}
          prospectId={prospectId}
          metricName="outfieldVelocity"
          metricCategory="position_player"
          metricEntries={metricEntries}
          currentUserId={currentUserId}
        />

        <MetricPillInput
          label={sport === "softball" ? "40 Time" : "60 Time"}
          value={metrics.sixtyTime ?? null}
          commonValues={COMMON_VALUES.sixtyTime}
          unit={METRIC_UNITS.time}
          onChange={(value) => onMetricChange("sixtyTime", value)}
          min={METRIC_RANGES.time.min}
          max={METRIC_RANGES.time.max}
          decimalPlaces={2}
          prospectId={prospectId}
          metricName="sixtyTime"
          metricCategory="position_player"
          metricEntries={metricEntries}
          currentUserId={currentUserId}
        />

        <MetricPillInput
          label="Catcher Velocity"
          value={metrics.catcherVelocity ?? null}
          commonValues={COMMON_VALUES.catcherVelocity}
          unit={METRIC_UNITS.velocity}
          onChange={(value) => onMetricChange("catcherVelocity", value)}
          min={METRIC_RANGES.velocity.min}
          max={METRIC_RANGES.velocity.max}
          decimalPlaces={1}
          prospectId={prospectId}
          metricName="catcherVelocity"
          metricCategory="position_player"
          metricEntries={metricEntries}
          currentUserId={currentUserId}
        />

        <MetricPillInput
          label="Bat Speed"
          value={metrics.batSpeed ?? null}
          commonValues={COMMON_VALUES.batSpeed}
          unit={METRIC_UNITS.velocity}
          onChange={(value) => onMetricChange("batSpeed", value)}
          min={METRIC_RANGES.velocity.min}
          max={METRIC_RANGES.velocity.max}
          decimalPlaces={1}
          prospectId={prospectId}
          metricName="batSpeed"
          metricCategory="position_player"
          metricEntries={metricEntries}
          currentUserId={currentUserId}
        />

        <MetricPillInput
          label="L-Grip"
          value={metrics.leftGripStrength ?? null}
          commonValues={COMMON_VALUES.leftGripStrength}
          unit={METRIC_UNITS.gripStrength}
          onChange={(value) => onMetricChange("leftGripStrength", value)}
          min={METRIC_RANGES.gripStrength.min}
          max={METRIC_RANGES.gripStrength.max}
          decimalPlaces={0}
          prospectId={prospectId}
          metricName="leftGripStrength"
          metricCategory="position_player"
          metricEntries={metricEntries}
          currentUserId={currentUserId}
        />

        <MetricPillInput
          label="R-Grip"
          value={metrics.rightGripStrength ?? null}
          commonValues={COMMON_VALUES.rightGripStrength}
          unit={METRIC_UNITS.gripStrength}
          onChange={(value) => onMetricChange("rightGripStrength", value)}
          min={METRIC_RANGES.gripStrength.min}
          max={METRIC_RANGES.gripStrength.max}
          decimalPlaces={0}
          prospectId={prospectId}
          metricName="rightGripStrength"
          metricCategory="position_player"
          metricEntries={metricEntries}
          currentUserId={currentUserId}
        />
      </div>
    </div>
  );
}
